<template>
  <v-row dense>
    <v-col cols="auto" style="width: 110px;">
      <v-select
        v-model="selected"
        item-value="id"
        item-text="country"
        :menu-props="menuProps"
        :items="selectItems"
        :loading="loading"
        :readonly="readonly"
        return-object
        :cache-items="true"
        @input="updateValue('code', $event)"
      >
        <template v-slot:selection="data">
          <v-avatar left tile size="25">
            <base-icon :name="data.item.iso"></base-icon>
          </v-avatar>
          <span class="ml-2">+{{ data.item.code }}</span>
        </template>
        <template v-slot:item="data">
          <template v-if="typeof data.item !== 'object'">
            <v-list-item-content v-text="data.item"></v-list-item-content>
          </template>
          <template v-else>
            <v-list-item-avatar tile>
              <base-icon :name="data.item.iso"></base-icon>
            </v-list-item-avatar>
            <v-list-item-content style="flex-wrap: nowrap; width: 220px;">
              <v-list-item-title
                class="d-flex flex-row overflow-visible"
                v-html="data.item.country"
              ></v-list-item-title>
              <v-list-item-subtitle
                class="justify-end d-flex flex-row"
                v-html="`+${data.item.code}`"
              ></v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </template>
      </v-select>
    </v-col>
    <v-col>
      <v-text-field
        v-mask="mask"
        :value="trimPhone()"
        :counter="counter"
        :hint="mask"
        :error-messages="errorMessages"
        :rules="rules"
        :readonly="readonly"
        :label="label"
        @input="updateValue('phone', $event)"
        @paste="onPaste"
      >
      </v-text-field>
    </v-col>
  </v-row>
</template>

<script>
import BaseIcon from './components/BaseIcon.vue';
import { mask } from 'vue-the-mask';
import { isEmpty } from 'lodash';
import { textLine } from '@/modules/_global/variables/rules';
import { mapState } from 'vuex';
import { mapCacheActions } from 'vuex-cache';
import { parsePhoneNumber } from 'awesome-phonenumber';

export default {
  name: 'PhoneInput',

  components: {
    BaseIcon
  },

  directives: {
    mask
  },

  filters: {},

  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [String, Array],
      default: () => []
    },
    rules: {
      type: Array,
      default: () => textLine
    },
    counter: {
      type: Number,
      default: 12
    },
    mask: {
      type: String,
      default: '############'
    },
    menuProps: {
      type: Object,
      default: () => ({ auto: true, overflowY: true })
    }
  },

  data: () => ({
    selected: {},
    phoneWithCode: null
  }),

  computed: {
    loading() {
      return this.$store.getters['loading'];
    },
    ...mapState('phone_flags', ['selectItems']),
    getDefaultCountry() {
      return this.selectItems.find(() => true);
    }
  },

  watch: {
    value: {
      deep: true,
      handler: 'setData'
    }
  },

  created() {
    this.getSelectItems().then(() => {
      this.setCode();
    });
  },

  methods: {
    ...mapCacheActions('phone_flags', ['getSelectItems']),

    onPaste(evt) {
      if (this.readonly) {
        return;
      }

      const data = evt.clipboardData.getData('text/plain');

      if (!isNaN(data[0])) {
        return;
      }

      const phone = `+${data.replace(/[^0-9]/g, '')}`;

      const pn = parsePhoneNumber(phone);

      if (pn.possible || pn.countryCode) {
        const codeItem = this.selectItems.find(
          el => el.code === pn.countryCode
        );

        if (codeItem) {
          this.selected = codeItem;
          this.updateValue('phone', pn.number.significant);
          evt.preventDefault();
        }
      }
    },

    trimCode() {
      if (!this.value) return this.getDefaultCountry.code;

      return parseInt(
        this.value
          .split('+')
          .pop()
          .split(' ')
          .shift(),
        10
      );
    },

    trimPhone() {
      if (!this.value) return '';
      return this.value.split(' ').pop();
    },

    updateValue(field, e) {
      if (field === 'phone' && e) {
        this.phoneWithCode = `+${this.selected.code} ${e}`;
      } else if (field === 'code' && e && this.trimPhone()) {
        this.phoneWithCode = `+${e.code} ${this.trimPhone()}`;
      } else {
        this.phoneWithCode = null;
      }

      if (this.phoneWithCode !== this.value) {
        // used $nextTick() because v-mask triggers input event twice and last event (masked value) not applied
        this.$nextTick(() => {
          this.$emit('input', this.phoneWithCode);
        });
      }
    },

    setData() {
      if (this.value !== null) {
        this.setCode();
      }
    },
    setCode() {
      this.selected = this.selectItems.find(el => {
        return el.code === this.trimCode();
      });
      if (isEmpty(this.selected)) {
        this.selected = this.getDefaultCountry;
      }
    }
  }
};
</script>
